/**
 * theme.js - theme framework core and component loader.
 * Entry module loaded by snippets/theme-scripts.liquid.
 *
 * Component registration rule: the vendor derives each custom element's tag from the
 * registerComponent key's FILENAME, kebab-casing its camelCase words ('ThemeCarousel' ->
 * <theme-carousel>). Keys need >= 2 camelCase words; single words are rejected at runtime.
 * The $info.meta.selector is descriptive only - the key is what defines the tag.
 *
 * Provides the shared services every component builds on:
 *   ThemeComponent            base class for all theme custom elements
 *   cart (Cart)               cart state with change-by-interaction events
 *   collection (CollectionService)  filter/sort/page state synced to the URL
 *   Blog (BlogService)        blog tag filtering and article search rendering
 *   OptionSelector family     variant option trees (manual + auto-resolving)
 *   formatMoney, dataLayerPush, hashUserDataForGoogle, scrollToElement,
 *   setVariantUrlParam
 *
 * The tail IIFE is the lazy component loader: it watches the DOM for known
 * custom elements and imports each component-X.js by exact filename on
 * first appearance - those filenames are load-bearing (import map in
 * snippets/theme-head.liquid).
 *
 * Carries local Kubix fixes to the blog search/filter market handling
 * (quoted market tag in search queries, market prefix from Shopify.routes,
 * and market tags excluded when rebuilding tagged/ filter URLs) - see git
 * history for the individual changes.
 *
 * Reconstructed to readable identifiers from the original minified vendor
 * bundle; behaviour verified unchanged by AST alpha-equivalence.
 */
var defineProperty = Object.defineProperty;
var setFunctionName = (target, functionName) =>
  defineProperty(target, "name", {
    value: functionName,
    configurable: !0,
  });
import {
  main as initFramework,
  BaseComponent,
  BaseCart,
  dist as vendorLib,
  queryString,
  axios,
} from "./vendor.js";
initFramework({
  enableLegacySalvoLiteComponentSupport: !0,
});
class ThemeComponent extends BaseComponent {}
setFunctionName(ThemeComponent, "ThemeComponent");
class Cart extends BaseCart {
  constructor() {
    (super(),
      (this._onChangeByInteraction = new vendorLib.SimpleEventDispatcher()));
  }
  get onChangeByInteraction() {
    return this._onChangeByInteraction.asEvent();
  }
  async asInteraction(action) {
    const result = await action();
    return (this._onChangeByInteraction.dispatch(), result);
  }
  isEmpty() {
    return cart.items.length <= 0;
  }
  getCartItemCount() {
    let count = 0;
    for (const item of cart.items) count += item.quantity;
    return count;
  }
}
setFunctionName(Cart, "Cart");
const cart = new Cart();
cart.reload().catch((error) => {});
class OptionSelector {
  constructor(product) {
    ((this.selectedValues = [null, null, null]),
      (this.variantTree = {}),
      (this.product = product),
      this.buildVariantTree());
  }
  buildVariantTree() {
    this.variantTree = {};
    for (const variant of this.product.variants) {
      const option1Value = variant.option1 || "",
        option2Value = variant.option2 || "",
        option3Value = variant.option3 || "";
      (this.variantTree[option1Value] || (this.variantTree[option1Value] = {}),
        this.variantTree[option1Value][option2Value] ||
          (this.variantTree[option1Value][option2Value] = {}),
        (this.variantTree[option1Value][option2Value][option3Value] = variant));
    }
  }
  findVariant(option1, option2, option3) {
    return (
      ((this.variantTree[option1 || ""] || {})[option2 || ""] || {})[
        option3 || ""
      ] || null
    );
  }
  select(option1, option2, option3) {
    this.selectedValues = [option1, option2, option3];
  }
}
setFunctionName(OptionSelector, "OptionSelector");
class ManualOptionSelector extends OptionSelector {
  get selected() {
    const options = this.options;
    return [
      options[0].values.filter(
        (optionValue) => optionValue.value == this.selectedValues[0],
      )[0],
      options[1].values.filter(
        (optionValue) => optionValue.value == this.selectedValues[1],
      )[0],
      options[2].values.filter(
        (optionValue) => optionValue.value == this.selectedValues[2],
      )[0],
    ];
  }
  set selected(selectedOptions) {
    this.selectedValues = [
      selectedOptions[0].value,
      selectedOptions[1].value,
      selectedOptions[2].value,
    ];
  }
  get options() {
    const selectedValues = this.selectedValues,
      [option1Label, option2Label, option3Label] = this.product.options,
      option1Values = this.product.variants.map((variant) => {
        var matchedVariant;
        return {
          label: variant.option1,
          value: variant.option1,
          selected: selectedValues[0] == variant.option1,
          outOfStock: !(
            (matchedVariant = this.findVariant(
              variant.option1,
              selectedValues[1],
              selectedValues[2],
            )) != null && matchedVariant.available
          ),
          variantExists: !!this.findVariant(
            variant.option1,
            selectedValues[1],
            selectedValues[2],
          ),
        };
      }),
      option2Values = this.product.variants.map((variant) => {
        var matchedVariant;
        return {
          label: variant.option2,
          value: variant.option2,
          selected: selectedValues[0] == variant.option2,
          outOfStock: !(
            (matchedVariant = this.findVariant(
              selectedValues[0],
              variant.option2,
              selectedValues[2],
            )) != null && matchedVariant.available
          ),
          variantExists: !!this.findVariant(
            selectedValues[0],
            variant.option2,
            selectedValues[2],
          ),
        };
      }),
      option3Values = this.product.variants.map((variant) => {
        var matchedVariant;
        return {
          label: variant.option3,
          value: variant.option3,
          selected: selectedValues[0] == variant.option3,
          outOfStock: !(
            (matchedVariant = this.findVariant(
              selectedValues[0],
              selectedValues[1],
              variant.option3,
            )) != null && matchedVariant.available
          ),
          variantExists: !!this.findVariant(
            selectedValues[0],
            selectedValues[1],
            variant.option3,
          ),
        };
      });
    return [
      {
        label: option1Label,
        values: option1Values,
      },
      {
        label: option2Label,
        values: option2Values,
      },
      {
        label: option3Label,
        values: option3Values,
      },
    ];
  }
}
setFunctionName(ManualOptionSelector, "ManualOptionSelector");
class AutoresolvingOptionSelector extends ManualOptionSelector {
  constructor() {
    (super(...arguments), (this._wasSelectionModified = !1));
  }
  get wasSelectionModified() {
    return this._wasSelectionModified;
  }
  select(option1, option2, option3) {
    const previousValues = this.selectedValues;
    (super.select(option1, option2, option3),
      (this._wasSelectionModified = this.resolveSelection(previousValues, [
        option1,
        option2,
        option3,
      ])));
  }
  resolveSelection(previousValues, nextValues) {
    if (this.findVariant(nextValues[0], nextValues[1], nextValues[2]))
      return !1;
    const changedSlots = [
        previousValues[0] != nextValues[0],
        previousValues[1] != nextValues[1],
        previousValues[2] != nextValues[2],
      ],
      options = this.options;
    let resolvedValues = null;
    if (!resolvedValues && !changedSlots[2]) {
      const candidate = options[2].values.filter(
        (optionValue) => optionValue.variantExists,
      )[0];
      candidate &&
        (resolvedValues = [nextValues[0], nextValues[1], candidate.value]);
    }
    if (!resolvedValues && !changedSlots[1]) {
      const candidate = options[1].values.filter(
        (optionValue) => optionValue.variantExists,
      )[0];
      candidate &&
        (resolvedValues = [nextValues[0], candidate.value, nextValues[2]]);
    }
    if (!resolvedValues && !changedSlots[0]) {
      const candidate = options[0].values.filter(
        (optionValue) => optionValue.variantExists,
      )[0];
      candidate &&
        (resolvedValues = [candidate.value, nextValues[1], nextValues[2]]);
    }
    return resolvedValues ? (this.select(...resolvedValues), !0) : !1;
  }
}
setFunctionName(AutoresolvingOptionSelector, "AutoresolvingOptionSelector");
const defaultCollectionState = {
  filters: {},
  sortBy: null,
  page: void 0,
};
class CollectionService {
  constructor() {
    ((this._onChange = new vendorLib.SimpleEventDispatcher()),
      (this._state = this.parseState()));
  }
  get state() {
    return this._state;
  }
  get onChange() {
    return this._onChange.asEvent();
  }
  parseState(url) {
    var locationRef, sortByRef;
    const state = defaultCollectionState;
    if (!url) {
      if (!((locationRef = window.location) != null && locationRef.href))
        return state;
      url = window.location.href;
    }
    const { query: query } = queryString.parseUrl(url);
    ((state.page = query.page ? Number(query.page) : void 0),
      (state.sortBy =
        ((sortByRef = query.sort_by) == null ? void 0 : sortByRef.toString()) ||
        null),
      (state.filters = {}));
    for (const paramName of Object.keys(query))
      if (paramName.indexOf("filter.") == 0) {
        const paramValue = query[paramName];
        if (!paramValue) continue;
        typeof paramValue == "string"
          ? (state.filters[paramName] = [paramValue])
          : (state.filters[paramName] = paramValue);
      }
    return state;
  }
  async render(sectionIds = []) {
    const { url: url, query: query } = queryString.parseUrl(
        window.location.href,
        {
          decode: !1,
        },
      ),
      sectionsUrl = queryString.stringifyUrl(
        {
          url: url,
          query: {
            ...query,
            sections: sectionIds
              .map((sectionId) => encodeURIComponent(sectionId))
              .join(","),
          },
        },
        {
          arrayFormat: "none",
          encode: !1,
        },
      );
    return (await axios.get(sectionsUrl)).data;
  }
  async update(newState) {
    var pageRef;
    const queryParams = {
      sort_by: newState.sortBy || void 0,
      page:
        ((pageRef = newState.page) == null ? void 0 : pageRef.toString()) ||
        void 0,
    };
    for (const filterKey of Object.keys(newState.filters))
      queryParams[filterKey] = newState.filters[filterKey].map((filterValue) =>
        encodeURIComponent(filterValue).replace(/%20/g, "+"),
      );
    const { url: url } = queryString.parseUrl(window.location.href),
      updatedUrl = queryString.stringifyUrl(
        {
          url: url,
          query: queryParams,
        },
        {
          arrayFormat: "none",
          encode: !1,
        },
      );
    (window.history.replaceState({}, document.title, updatedUrl),
      (this._state = newState),
      this._onChange.dispatch(newState));
  }
}
setFunctionName(CollectionService, "CollectionService");
const collection = new CollectionService();
function scrollToElement(element, alignToTop = !1) {
  if (element === null) return;
  const rect = element.getBoundingClientRect();
  let targetScrollTop = rect.top;
  (alignToTop ||
    (targetScrollTop = rect.top + window.pageYOffset - window.innerHeight / 2),
    window.scroll({
      top: targetScrollTop,
      left: 0,
      behavior: "smooth",
    }));
}
setFunctionName(scrollToElement, "scrollToElement$1");
function dataLayerPush(eventData = null) {
  var dataLayerRef;
  eventData != null &&
    ((window.dataLayer &&
      window.dataLayer.find((entry) => entry.event === "view_cart") &&
      eventData.event === "view_cart") ||
      (dataLayerRef = window.dataLayer) == null ||
      dataLayerRef.push(eventData));
}
setFunctionName(dataLayerPush, "dataLayerPush");
function sha256HexPolyfill(input) {
  function rightRotate(value, amount) {
    return (value >>> amount) | (value << (32 - amount));
  }
  setFunctionName(rightRotate, "rightRotate");
  const pow = Math.pow,
    maxWord = pow(2, 32),
    lengthKey = "length",
    roundConstants = [],
    hashValues = [];
  let primeCount = 0;
  const isPrime = setFunctionName((candidate) => {
      for (
        let divisor = 2, limit = Math.sqrt(candidate);
        divisor <= limit;
        divisor++
      )
        if (candidate % divisor === 0) return !1;
      return !0;
    }, "isPrime"),
    fracBits = setFunctionName(
      (value) => ((value - Math.floor(value)) * maxWord) | 0,
      "fracBits",
    );
  for (let candidate = 2; primeCount < 64; candidate++)
    isPrime(candidate) &&
      (primeCount < 8 &&
        (hashValues[primeCount] = fracBits(Math.pow(candidate, 1 / 2))),
      (roundConstants[primeCount] = fracBits(Math.pow(candidate, 1 / 3))),
      primeCount++);
  const messageBytes = new TextEncoder().encode(input),
    words = [];
  let index;
  for (index = 0; index < messageBytes[lengthKey]; index++)
    words[index >> 2] |= messageBytes[index] << (((3 - index) % 4) * 8);
  const bitLength = messageBytes[lengthKey] * 8;
  ((words[(bitLength >> 5) | 0] |= 128 << (24 - (bitLength % 32))),
    (words[(((bitLength + 64) >> 9) << 4) + 15] = bitLength));
  const messageSchedule = new Array(64);
  let regA, regB, regC, regD, regE, regF, regG, regH;
  for (let blockOffset = 0; blockOffset < words[lengthKey]; blockOffset += 16) {
    for (
      regA = hashValues[0],
        regB = hashValues[1],
        regC = hashValues[2],
        regD = hashValues[3],
        regE = hashValues[4],
        regF = hashValues[5],
        regG = hashValues[6],
        regH = hashValues[7],
        index = 0;
      index < 64;
      index++
    ) {
      if (index < 16) messageSchedule[index] = words[index + blockOffset] | 0;
      else {
        const wordBack15 = messageSchedule[index - 15],
          wordBack2 = messageSchedule[index - 2],
          lowerSigma0 =
            rightRotate(wordBack15, 7) ^
            rightRotate(wordBack15, 18) ^
            (wordBack15 >>> 3),
          lowerSigma1 =
            rightRotate(wordBack2, 17) ^
            rightRotate(wordBack2, 19) ^
            (wordBack2 >>> 10);
        messageSchedule[index] =
          (messageSchedule[index - 16] +
            lowerSigma0 +
            messageSchedule[index - 7] +
            lowerSigma1) |
          0;
      }
      const choice = (regE & regF) ^ (~regE & regG),
        majority = (regA & regB) ^ (regA & regC) ^ (regB & regC),
        upperSigmaA =
          rightRotate(regA, 2) ^ rightRotate(regA, 13) ^ rightRotate(regA, 22),
        upperSigmaE =
          rightRotate(regE, 6) ^ rightRotate(regE, 11) ^ rightRotate(regE, 25),
        temp1 =
          (((regH + upperSigmaE + choice + roundConstants[index]) | 0) +
            messageSchedule[index]) |
          0,
        temp2 = (upperSigmaA + majority) | 0;
      ((regH = regG),
        (regG = regF),
        (regF = regE),
        (regE = (regD + temp1) | 0),
        (regD = regC),
        (regC = regB),
        (regB = regA),
        (regA = (temp1 + temp2) | 0));
    }
    ((hashValues[0] = (hashValues[0] + regA) | 0),
      (hashValues[1] = (hashValues[1] + regB) | 0),
      (hashValues[2] = (hashValues[2] + regC) | 0),
      (hashValues[3] = (hashValues[3] + regD) | 0),
      (hashValues[4] = (hashValues[4] + regE) | 0),
      (hashValues[5] = (hashValues[5] + regF) | 0),
      (hashValues[6] = (hashValues[6] + regG) | 0),
      (hashValues[7] = (hashValues[7] + regH) | 0));
  }
  const hexPairs = [];
  for (index = 0; index < hashValues[lengthKey]; index++)
    for (let byteOffset = 3; byteOffset + 1; byteOffset--) {
      const byteValue = (hashValues[index] >> (byteOffset * 8)) & 255;
      hexPairs.push((byteValue < 16 ? "0" : "") + byteValue.toString(16));
    }
  return hexPairs.join("");
}
setFunctionName(sha256HexPolyfill, "sha256HexPolyfill");
async function sha256Hex(input) {
  try {
    const messageBytes = new TextEncoder().encode(input),
      subtleCrypto = window.crypto && window.crypto.subtle;
    if (!subtleCrypto) return sha256HexPolyfill(input);
    const digestBuffer = await subtleCrypto.digest("SHA-256", messageBytes);
    return Array.from(new Uint8Array(digestBuffer))
      .map((byte) => byte.toString(16).padStart(2, "0"))
      .join("");
  } catch {
    try {
      return sha256HexPolyfill(input);
    } catch {
      return null;
    }
  }
}
setFunctionName(sha256Hex, "sha256Hex");
function normalizeEmail(email) {
  return typeof email != "string" || !email ? null : email.trim().toLowerCase();
}
setFunctionName(normalizeEmail, "normalizeEmail");
function normalizePhone(phone) {
  return typeof phone != "string" || !phone
    ? null
    : phone.replace(/\D+/g, "") || null;
}
setFunctionName(normalizePhone, "normalizePhone");
function normalizeText(text) {
  return typeof text != "string" || !text ? null : text.trim().toLowerCase();
}
setFunctionName(normalizeText, "normalizeText");
const userDataNormalizers = {
  user_email: normalizeEmail,
  user_phone: normalizePhone,
  user_firstname: normalizeText,
  user_lastname: normalizeText,
  user_street: normalizeText,
  user_zipcode: normalizeText,
  user_country: normalizeText,
};
async function hashUserDataForGoogle(userData) {
  const hashedFields = {};
  if (!userData) return hashedFields;
  const normalizerEntries = Object.entries(userDataNormalizers);
  for (const [fieldName, normalizer] of normalizerEntries)
    if (Object.prototype.hasOwnProperty.call(userData, fieldName)) {
      const normalizedValue = normalizer(userData[fieldName]);
      if (normalizedValue) {
        const hashedValue = await sha256Hex(normalizedValue);
        hashedValue &&
          /^[0-9a-f]{64}$/.test(hashedValue) &&
          (hashedFields[fieldName] = hashedValue);
      }
    }
  return hashedFields;
}
setFunctionName(hashUserDataForGoogle, "hashUserDataForGoogle");
function scrollToElementDuplicate(element, alignToTop = !1) {
  if (element === null) return;
  const rect = element.getBoundingClientRect();
  let targetScrollTop = rect.top;
  (alignToTop ||
    (targetScrollTop = rect.top + window.pageYOffset - window.innerHeight / 2),
    window.scroll({
      top: targetScrollTop,
      left: 0,
      behavior: "smooth",
    }));
}
setFunctionName(scrollToElementDuplicate, "scrollToElement");
class BlogService {
  async render(sectionIds = [], blogHandle, market, searchTerm) {
    if (searchTerm && searchTerm.trim())
      return this.search(sectionIds, searchTerm, market);
    let taggedPath = `tagged/market-${market}`;
    const baseUrl = window.location.href.split("/blogs/")[0],
      taggedPart = window.location.href.split("/tagged/")[1],
      existingTags = taggedPart ? taggedPart.split("?")[0] : "";
    if (existingTags && existingTags !== "") {
      const marketTag = `market-${market}`,
        topicTags = existingTags
          .split("+")
          .filter(Boolean)
          .filter((tag) => tag !== marketTag && !/^market-/i.test(tag));
      taggedPath =
        `tagged/${marketTag}` +
        (topicTags.length ? `+${topicTags.join("+")}` : "");
    }
    const { url: url, query: query } = queryString.parseUrl(
        `${baseUrl}/blogs/${blogHandle}/${taggedPath}`,
        {
          decode: !1,
        },
      ),
      page = new URLSearchParams(window.location.search).get("page") || 1,
      sectionsUrl = queryString.stringifyUrl(
        {
          url: url,
          query: {
            ...query,
            sections: sectionIds
              .map((sectionId) => encodeURIComponent(sectionId))
              .join(","),
            page: page,
          },
        },
        {
          arrayFormat: "none",
          encode: !1,
        },
      );
    return (await axios.get(sectionsUrl)).data;
  }
  async search(sectionIds = [], searchTerm, market) {
    var textContentRef, errorConfigRef, errorRequestRef;
    // Search the visitor's own market, so the results section renders with that market's localization and its article gate applies hide tags.
    const origin = `${window.location.origin}${((window.Shopify && Shopify.routes && Shopify.routes.root) || "/").replace(/\/$/, "")}`,
      searchParams = new URLSearchParams(window.location.search),
      page = searchParams.get("page") || "1",
      marketTagFilter = market ? `tag:"market-${market}"` : "",
      fullQuery = marketTagFilter
        ? `${searchTerm} ${marketTagFilter}`.trim()
        : searchTerm,
      searchPageUrl = queryString.stringifyUrl(
        {
          url: `${origin}/search`,
          query: {
            type: "article",
            q: fullQuery,
            page: page,
            "options[prefix]": "last",
            section_id: "search-results",
          },
        },
        {
          arrayFormat: "none",
          encode: !1,
        },
      );
    try {
      const sectionApiUrl = queryString.stringifyUrl(
        {
          url: `${origin}/search`,
          query: {
            type: "article",
            q: fullQuery,
            page: page,
            "options[prefix]": "last",
            sections: "search-results",
            section_id: "search-results",
          },
        },
        {
          arrayFormat: "none",
          encode: !1,
        },
      );
      let resultDocument, responseHtml;
      try {
        const sectionResponse = await axios.get(sectionApiUrl, {
          headers: {
            Accept: "application/json",
          },
        });
        if (
          typeof sectionResponse.data == "object" &&
          sectionResponse.data["search-results"]
        )
          responseHtml = sectionResponse.data["search-results"];
        else {
          const pageResponse = await axios.get(searchPageUrl, {
            headers: {
              Accept: "text/html",
            },
          });
          responseHtml =
            typeof pageResponse.data == "string"
              ? pageResponse.data
              : JSON.stringify(pageResponse.data);
        }
      } catch {
        try {
          const fallbackResponse = await axios.get(searchPageUrl, {
            headers: {
              Accept: "text/html",
            },
          });
          responseHtml =
            typeof fallbackResponse.data == "string"
              ? fallbackResponse.data
              : JSON.stringify(fallbackResponse.data);
        } catch (fetchError) {
          throw (
            console.error(
              "Both section API and full page fetch failed:",
              fetchError.message,
            ),
            fetchError
          );
        }
      }
      if (!responseHtml || responseHtml.length === 0)
        throw new Error("Empty HTML response received");
      resultDocument = new DOMParser().parseFromString(
        responseHtml,
        "text/html",
      );
      const articleCards = resultDocument.querySelectorAll("article-card");
      if (articleCards.length > 0)
        try {
          const localeMatch = window.location.pathname.match(
              /^\/[a-zA-Z]{2}-[a-zA-Z0-9-]+\//,
            ),
            marketPrefix = (
              (window.Shopify && Shopify.routes && Shopify.routes.root) ||
              (localeMatch ? localeMatch[0] : "/")
            ).replace(/\/$/, ""),
            cardsHtml = Array.from(articleCards)
              .map((card, cardIndex) => {
                try {
                  let cardHtml;
                  if (card.outerHTML) cardHtml = card.outerHTML;
                  else if (card.innerHTML) {
                    const tagName = card.tagName.toLowerCase();
                    cardHtml = `<${tagName}${Array.from(card.attributes)
                      .map(
                        (attribute) =>
                          ` ${attribute.name}="${attribute.value}"`,
                      )
                      .join("")}>${card.innerHTML}</${tagName}>`;
                  } else return "";
                  if (!cardHtml || cardHtml.trim().length === 0) return "";
                  if (marketPrefix) {
                    const container = document.createElement("div");
                    ((container.innerHTML = cardHtml),
                      container
                        .querySelectorAll("a[href]")
                        .forEach((anchor) => {
                          const href = anchor.getAttribute("href");
                          if (!href) return;
                          const parsedUrl = href.startsWith("http")
                              ? new URL(href)
                              : new URL(href, window.location.origin),
                            pathname = parsedUrl.pathname;
                          pathname.startsWith("/blogs/") &&
                            !pathname.startsWith(marketPrefix) &&
                            ((parsedUrl.pathname = `${marketPrefix}${pathname}`),
                            anchor.setAttribute(
                              "href",
                              `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`,
                            ));
                        }),
                      (cardHtml = container.innerHTML));
                  }
                  return `<div class="blog-main__article-item js-blog-main-article-item">${cardHtml}</div>`;
                } catch {
                  return "";
                }
              })
              .filter((html) => html && html.length > 0)
              .join("");
          if (!cardsHtml || cardsHtml.length === 0)
            throw new Error(
              "Failed to extract HTML from article cards - all cards produced empty HTML",
            );
          let paginationHtml = "",
            totalCount = articleCards.length;
          const paginationBar = resultDocument.querySelector(
            'pagination-bar, [data-ref="paginationBar"]',
          );
          if (paginationBar) {
            paginationHtml = paginationBar.outerHTML;
            const pageAnchors =
              paginationBar.querySelectorAll('a[href*="page"]');
            let maxPage = 1;
            pageAnchors.forEach((anchor) => {
              const href = anchor.getAttribute("href");
              if (href)
                try {
                  const parsedUrl = href.startsWith("http")
                      ? new URL(href)
                      : new URL(href, window.location.origin),
                    pageNumber = parseInt(
                      parsedUrl.searchParams.get("page") || "1",
                    );
                  pageNumber > maxPage && (maxPage = pageNumber);
                } catch {}
            });
            const currentPageElement = paginationBar.querySelector(
              ".current, .page.current",
            );
            if (currentPageElement) {
              const currentText =
                  (textContentRef = currentPageElement.textContent) == null
                    ? void 0
                    : textContentRef.trim(),
                currentNumber = parseInt(currentText || "1");
              currentNumber > maxPage && (maxPage = currentNumber);
            }
            const pageSize = 20;
            maxPage > 1
              ? (totalCount = (maxPage - 1) * pageSize + articleCards.length)
              : (totalCount = articleCards.length);
            const productCountElement = resultDocument.querySelector(
              ".collection-filtering-sorting__product-count",
            );
            if (productCountElement) {
              const countMatch = (productCountElement.textContent || "").match(
                /(\d+)/,
              );
              countMatch && (totalCount = parseInt(countMatch[1]));
            }
            const origin = window.location.origin,
              blogPathMatch = window.location.pathname.match(
                /^(\/[a-zA-Z-]+)?\/blogs\/[^\/]+/,
              ),
              blogPath = blogPathMatch ? blogPathMatch[0] : "/blogs/blog",
              searchQuery = searchParams.get("q") || "",
              currentPage = parseInt(searchParams.get("page") || "1"),
              container = document.createElement("div");
            ((container.innerHTML = paginationHtml),
              container.querySelectorAll("a").forEach((anchor) => {
                var textRef;
                const href = anchor.getAttribute("href");
                if (href)
                  try {
                    const absoluteUrl = href.startsWith("http")
                      ? href
                      : href.startsWith("/")
                        ? `${origin}${href}`
                        : `${origin}/${href}`;
                    let pageParam = new URL(absoluteUrl).searchParams.get(
                      "page",
                    );
                    if (!pageParam) {
                      const anchorText =
                          (textRef = anchor.textContent) == null
                            ? void 0
                            : textRef.trim(),
                        cleanedText =
                          anchorText == null
                            ? void 0
                            : anchorText
                                .replace(/Next|Previous|chevron|icon/gi, "")
                                .trim(),
                        numberMatch =
                          cleanedText == null
                            ? void 0
                            : cleanedText.match(/^\d+$/);
                      if (numberMatch) pageParam = numberMatch[0];
                      else {
                        const isNextButton =
                            anchor.classList.contains("next") ||
                            anchor.querySelector(".pagination__next") ||
                            anchor.querySelector(".pagination__icon--next"),
                          isPrevButton =
                            anchor.classList.contains("prev") ||
                            anchor.querySelector(".pagination__previous") ||
                            anchor.querySelector(".pagination__icon--prev");
                        if (isNextButton)
                          pageParam = (currentPage + 1).toString();
                        else if (isPrevButton)
                          pageParam = Math.max(1, currentPage - 1).toString();
                        else return;
                      }
                    }
                    const rebuiltUrl = new URL(`${origin}${blogPath}`);
                    (searchQuery &&
                      rebuiltUrl.searchParams.set("q", searchQuery),
                      pageParam &&
                        pageParam !== "1" &&
                        rebuiltUrl.searchParams.set("page", pageParam),
                      anchor.setAttribute(
                        "href",
                        `${rebuiltUrl.pathname}${rebuiltUrl.search}`,
                      ));
                  } catch {}
              }),
              (paginationHtml = container.innerHTML),
              (paginationHtml = paginationHtml.replace(
                /<pagination-bar([^>]*)>/,
                `<pagination-bar$1 data-total-count="${totalCount}">`,
              )));
          }
          return {
            "blog-main": `<div class="blog-main__articles" data-ref="articlesContainer" data-total-count="${totalCount}">${cardsHtml}</div>${paginationHtml}`,
          };
        } catch {}
      const articleLinks = [],
        fallbackCards = resultDocument.querySelectorAll(
          'article-card, [is="article-card"]',
        );
      (fallbackCards.forEach((card) => {
        const articleAnchor = card.querySelector(
          'a[href*="/articles/"], a[href*="/blogs/"]',
        );
        if (articleAnchor) {
          const href = articleAnchor.getAttribute("href");
          href &&
            (href.includes("/articles/") ||
              (!href.match(/\/blogs\/[^\/]+$/) &&
                !href.includes("/tagged/"))) &&
            articleLinks.push(articleAnchor);
        }
      }),
        resultDocument
          .querySelectorAll('a[href*="/blogs/"], a[href*="/articles/"]')
          .forEach((anchor) => {
            const href = anchor.getAttribute("href");
            if (!href) return;
            const isBlogLink = href.includes("/blogs/"),
              isExcludedLink =
                href.includes("/products/") ||
                href.includes("/pages/") ||
                href.includes("/collections/") ||
                href.includes("/cart") ||
                href.includes("/checkout") ||
                href.includes("/tagged/"),
              pathSegments = href
                .split("?")[0]
                .split("#")[0]
                .split("/")
                .filter((segment) => segment.length > 0),
              blogsIndex = pathSegments.indexOf("blogs"),
              isBlogRootLink =
                blogsIndex >= 0 && pathSegments.length <= blogsIndex + 2;
            ((isBlogLink && !isExcludedLink && !isBlogRootLink) ||
              href.includes("/articles/")) &&
              (articleLinks.some((existingAnchor) => {
                const existingHref = existingAnchor.getAttribute("href");
                return (
                  existingHref === href ||
                  (existingHref &&
                    href &&
                    existingHref
                      .replace(/^https?:\/\//, "")
                      .replace(/^\/+/, "") ===
                      href.replace(/^https?:\/\//, "").replace(/^\/+/, ""))
                );
              }) ||
                articleLinks.push(anchor));
          }),
        fallbackCards.length === 0 &&
          resultDocument
            .querySelectorAll(
              '.product-card, [class*="product-card"], product-card',
            )
            .forEach((card) => {
              const blogAnchor = card.querySelector(
                'a[href*="/blogs/"], a[href*="/articles/"]',
              );
              if (blogAnchor) {
                const href = blogAnchor.getAttribute("href");
                if (href) {
                  const isBlogLink = href.includes("/blogs/"),
                    isExcludedLink =
                      href.includes("/products/") ||
                      href.includes("/pages/") ||
                      href.includes("/collections/") ||
                      href.includes("/cart") ||
                      href.includes("/checkout") ||
                      href.includes("/tagged/") ||
                      href.match(/\/blogs\/[^\/]+$/);
                  ((isBlogLink && !isExcludedLink) ||
                    href.includes("/articles/")) &&
                    (articleLinks.some(
                      (existingAnchor) =>
                        existingAnchor.getAttribute("href") === href,
                    ) ||
                      articleLinks.push(blogAnchor));
                }
              }
            }),
        articleLinks.length === 0 &&
          resultDocument
            .querySelectorAll(
              '*[href*="/articles/"], *[data-href*="/articles/"]',
            )
            .forEach((element) => {
              const href =
                element.getAttribute("href") ||
                element.getAttribute("data-href");
              if (
                href &&
                href.includes("/articles/") &&
                !href.includes("/products/")
              ) {
                const anchorElement = element.tagName === "A" ? element : null;
                if (anchorElement && !articleLinks.includes(anchorElement))
                  articleLinks.push(anchorElement);
                else if (!anchorElement) {
                  const closestAnchor =
                    element.closest("a") || element.querySelector("a");
                  closestAnchor &&
                    !articleLinks.includes(closestAnchor) &&
                    articleLinks.push(closestAnchor);
                }
              }
            }));
      const articlesByUrl = new Map();
      articleLinks.length > 0 &&
        articleLinks.forEach((anchor) => {
          var textRef,
            titleElementRef,
            titleTextRef,
            anchorRef,
            anchorTextRef,
            slugRef,
            srcsetRef,
            srcsetFirstRef,
            excerptElementRef,
            excerptTextRef;
          const href = anchor.getAttribute("href");
          if (!href || href.includes("/products/")) return;
          const absoluteUrl = href.startsWith("http")
            ? href
            : `${origin}${href}`;
          if (articlesByUrl.has(absoluteUrl)) return;
          let title =
            ((textRef = anchor.textContent) == null
              ? void 0
              : textRef.trim()) || "";
          const cardContainer = anchor.closest(
            'article, article-card, .article-card, [class*="article-card"], .product-card, [class*="product-card"], [class*="card"], .search-result, [class*="search-result"]',
          );
          (!title || title.length < 3) &&
            (cardContainer &&
              (title =
                ((titleTextRef =
                  (titleElementRef = cardContainer.querySelector(
                    'h1, h2, h3, h4, .title, [class*="title"], .product-card__title, .article-card__title',
                  )) == null
                    ? void 0
                    : titleElementRef.textContent) == null
                  ? void 0
                  : titleTextRef.trim()) ||
                ((anchorTextRef =
                  (anchorRef = cardContainer.querySelector(
                    'a[href*="/articles/"]',
                  )) == null
                    ? void 0
                    : anchorRef.textContent) == null
                  ? void 0
                  : anchorTextRef.trim()) ||
                title),
            (!title || title.length < 3) &&
              (title =
                anchor.getAttribute("title") ||
                anchor.getAttribute("aria-label") ||
                ((slugRef = href.split("/").pop()) == null
                  ? void 0
                  : slugRef.replace(/-/g, " ").replace(/\.html$/, "")) ||
                "Untitled Article"));
          let imageUrl = "";
          if (cardContainer) {
            const imageElement = cardContainer.querySelector("img");
            if (
              (imageElement &&
                (imageUrl =
                  imageElement.getAttribute("src") ||
                  imageElement.getAttribute("data-src") ||
                  imageElement.getAttribute("data-lazy-src") ||
                  imageElement.getAttribute("data-original") ||
                  ((srcsetFirstRef =
                    (srcsetRef = imageElement.getAttribute("data-srcset")) ==
                    null
                      ? void 0
                      : srcsetRef.split(",")[0]) == null
                    ? void 0
                    : srcsetFirstRef.trim().split(" ")[0]) ||
                  ""),
              !imageUrl)
            ) {
              const backgroundElement = cardContainer.querySelector(
                '[style*="background-image"]',
              );
              if (backgroundElement) {
                const backgroundUrlMatch = (
                  backgroundElement.getAttribute("style") || ""
                ).match(/url\(['"]?([^'")]+)/);
                backgroundUrlMatch &&
                  backgroundUrlMatch[1] &&
                  (imageUrl = backgroundUrlMatch[1]);
              }
            }
          }
          let excerpt = "";
          cardContainer &&
            ((excerpt =
              ((excerptTextRef =
                (excerptElementRef = cardContainer.querySelector(
                  '.excerpt, [class*="excerpt"], .description, [class*="description"], p:not(.visually-hidden)',
                )) == null
                  ? void 0
                  : excerptElementRef.textContent) == null
                ? void 0
                : excerptTextRef.trim()) || ""),
            excerpt.length > 150 &&
              (excerpt = excerpt.substring(0, 150) + "..."));
          const absoluteImageUrl = imageUrl
            ? imageUrl.startsWith("http")
              ? imageUrl
              : `${origin}${imageUrl}`
            : void 0;
          articlesByUrl.set(absoluteUrl, {
            url: absoluteUrl,
            title: title.substring(0, 200),
            image: absoluteImageUrl,
            excerpt: excerpt.substring(0, 150),
          });
        });
      const articles = Array.from(articlesByUrl.values());
      if (articles.length === 0)
        return {
          "blog-main":
            '<div class="blog-main__articles" data-ref="articlesContainer"><p class="blog-main__no-results text-center text-small">No articles found matching your search.</p></div>',
        };
      const localeMatch = window.location.pathname.match(
          /^\/[a-zA-Z]{2}-[a-zA-Z0-9-]+\//,
        ),
        marketPrefix = (
          (window.Shopify && Shopify.routes && Shopify.routes.root) ||
          (localeMatch ? localeMatch[0] : "/")
        ).replace(/\/$/, ""),
        articlesHtml = articles
          .map((article) => {
            const escapeHtml = setFunctionName((value) => {
              const container = document.createElement("div");
              return ((container.textContent = value), container.innerHTML);
            }, "escapeHtml");
            let articleUrl = escapeHtml(article.url);
            try {
              const parsedUrl = articleUrl.startsWith("http")
                ? new URL(articleUrl)
                : new URL(articleUrl, window.location.origin);
              marketPrefix &&
                parsedUrl.pathname.startsWith("/blogs/") &&
                !parsedUrl.pathname.startsWith(marketPrefix) &&
                ((parsedUrl.pathname = `${marketPrefix}${parsedUrl.pathname}`),
                (articleUrl = `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`));
            } catch {}
            return `
                    <div class="blog-main__article-item js-blog-main-article-item">
                        <article-card class="article-card flex flex-column gap-12 rounded p-16 pt-24 shadow-sm blog-main__article">
                            ${article.image ? `<img src="${escapeHtml(article.image)}" alt="${escapeHtml(article.title)}" class="article-card__image" loading="lazy">` : ""}
                            <div class="article-card__content">
                                <h2 class="article-card__title">
                                    <a href="${articleUrl}" class="text-decoration-none">${escapeHtml(article.title)}</a>
                                </h2>
                                ${article.excerpt ? `<p class="article-card__excerpt">${escapeHtml(article.excerpt)}</p>` : ""}
                                <a href="${articleUrl}" class="button button--primary button--small button--card-cta article-card__button">
                                    Read more
                                </a>
                            </div>
                        </article-card>
                    </div>
                `;
          })
          .join("");
      let paginationHtml = "",
        totalCount = articles.length;
      const paginationBar = resultDocument.querySelector(
        'pagination-bar, [data-ref="paginationBar"]',
      );
      if (paginationBar) {
        paginationHtml = paginationBar.outerHTML;
        const pageAnchors = paginationBar.querySelectorAll('a[href*="page"]');
        let maxPage = 1;
        pageAnchors.forEach((anchor) => {
          const href = anchor.getAttribute("href");
          if (href)
            try {
              const parsedUrl = href.startsWith("http")
                  ? new URL(href)
                  : new URL(href, window.location.origin),
                pageNumber = parseInt(
                  parsedUrl.searchParams.get("page") || "1",
                );
              pageNumber > maxPage && (maxPage = pageNumber);
            } catch {}
        });
        const pageSize = 20;
        maxPage > 1
          ? (totalCount = (maxPage - 1) * pageSize + articles.length)
          : (totalCount = articles.length);
        const productCountElement = resultDocument.querySelector(
          ".collection-filtering-sorting__product-count",
        );
        if (productCountElement) {
          const countMatch = (productCountElement.textContent || "").match(
            /(\d+)/,
          );
          countMatch && (totalCount = parseInt(countMatch[1]));
        }
        const origin = window.location.origin,
          blogPathMatch = window.location.pathname.match(
            /^(\/[a-zA-Z-]+)?\/blogs\/[^\/]+/,
          ),
          blogPath = blogPathMatch ? blogPathMatch[0] : "/blogs/blog",
          searchQuery = searchParams.get("q") || "",
          currentPage = parseInt(searchParams.get("page") || "1"),
          container = document.createElement("div");
        ((container.innerHTML = paginationHtml),
          container.querySelectorAll("a").forEach((anchor) => {
            var textRef;
            const href = anchor.getAttribute("href");
            if (href)
              try {
                const absoluteUrl = href.startsWith("http")
                  ? href
                  : href.startsWith("/")
                    ? `${origin}${href}`
                    : `${origin}/${href}`;
                let pageParam = new URL(absoluteUrl).searchParams.get("page");
                if (!pageParam) {
                  const anchorText =
                      (textRef = anchor.textContent) == null
                        ? void 0
                        : textRef.trim(),
                    cleanedText =
                      anchorText == null
                        ? void 0
                        : anchorText
                            .replace(/Next|Previous|chevron|icon/gi, "")
                            .trim(),
                    numberMatch =
                      cleanedText == null ? void 0 : cleanedText.match(/^\d+$/);
                  if (numberMatch) pageParam = numberMatch[0];
                  else {
                    const isNextButton =
                        anchor.classList.contains("next") ||
                        anchor.querySelector(".pagination__next") ||
                        anchor.querySelector(".pagination__icon--next"),
                      isPrevButton =
                        anchor.classList.contains("prev") ||
                        anchor.querySelector(".pagination__previous") ||
                        anchor.querySelector(".pagination__icon--prev");
                    if (isNextButton) pageParam = (currentPage + 1).toString();
                    else if (isPrevButton)
                      pageParam = Math.max(1, currentPage - 1).toString();
                    else return;
                  }
                }
                const rebuiltUrl = new URL(`${origin}${blogPath}`);
                (searchQuery && rebuiltUrl.searchParams.set("q", searchQuery),
                  pageParam &&
                    pageParam !== "1" &&
                    rebuiltUrl.searchParams.set("page", pageParam),
                  anchor.setAttribute(
                    "href",
                    `${rebuiltUrl.pathname}${rebuiltUrl.search}`,
                  ));
              } catch {}
          }),
          (paginationHtml = container.innerHTML),
          (paginationHtml = paginationHtml.replace(
            /<pagination-bar([^>]*)>/,
            `<pagination-bar$1 data-total-count="${totalCount}">`,
          )));
      }
      return {
        "blog-main": `<div class="blog-main__articles" data-ref="articlesContainer" data-total-count="${totalCount}">${articlesHtml}</div>${paginationHtml}`,
      };
    } catch (error) {
      return (
        console.error("Error fetching search results:", error),
        error instanceof Error &&
          (console.error("Error message:", error.message),
          console.error("Error stack:", error.stack)),
        error != null &&
          error.response &&
          (console.error("Axios error response:", error.response.data),
          console.error("Axios error status:", error.response.status),
          console.error(
            "Axios error URL:",
            ((errorConfigRef = error.config) == null
              ? void 0
              : errorConfigRef.url) ||
              ((errorRequestRef = error.request) == null
                ? void 0
                : errorRequestRef.responseURL),
          )),
        error != null &&
          error.request &&
          console.error(
            "Request made but no response received:",
            error.request,
          ),
        {
          "blog-main":
            '<div class="blog-main__articles" data-ref="articlesContainer"><p class="text-center">Error loading search results. Please try again.</p></div>',
        }
      );
    }
  }
}
setFunctionName(BlogService, "BlogService");
const blog = new BlogService();
function setVariantUrlParam(variantId) {
  const { url: url, query: query } = queryString.parseUrl(window.location.href),
    updatedUrl = queryString.stringifyUrl(
      {
        url: url,
        query: {
          ...query,
          variant: variantId,
        },
      },
      {
        skipEmptyString: !0,
        skipNull: !0,
      },
    );
  // parseUrl drops the fragment, and this runs on load, so without it a link to a section on the page lands at the top.
  window.history &&
    window.history.replaceState({}, document.title, updatedUrl + window.location.hash);
}
setFunctionName(setVariantUrlParam, "setVariantUrlParam");
// A section's full id carries a template number that changes between themes, so links name the section by its key alone (#featured_video_n4WHwH).
function scrollToSectionByKey() {
  const key = window.location.hash.slice(1);
  if (!/^[\w-]+$/.test(key)) return;
  const fullIdSection = document.getElementById(key);
  // Other in-page anchors keep the browser's native behaviour; only sections get the hold.
  if (fullIdSection && !key.startsWith("shopify-section-")) return;
  const section = fullIdSection || document.querySelector(`[id^="shopify-section-"][id$="__${key}"]`);
  if (!section) return;
  section.scrollIntoView();
  holdSectionInView(section);
}
// Late media and third-party widgets can move the page just after arrival, so the section is put back until the visitor scrolls themselves.
function holdSectionInView(section) {
  const visitorInputs = ["wheel", "pointerdown", "keydown"];
  const realign = () => Math.abs(section.getBoundingClientRect().top) > 2 && section.scrollIntoView();
  const release = () => {
    window.removeEventListener("scroll", realign);
    visitorInputs.forEach((type) => window.removeEventListener(type, release));
  };
  window.addEventListener("scroll", realign, { passive: true });
  visitorInputs.forEach((type) => window.addEventListener(type, release, { passive: true }));
  const releaseAfterLoad = () => setTimeout(release, 2500);
  document.readyState === "complete" ? releaseAfterLoad() : window.addEventListener("load", releaseAfterLoad, { once: true });
}
// Sections are server-rendered, so this needn't wait for load, which waits on the video iframes and can take seconds on a phone.
document.readyState === "loading" ? document.addEventListener("DOMContentLoaded", scrollToSectionByKey) : scrollToSectionByKey();
window.addEventListener("hashchange", scrollToSectionByKey);
function formatMoney(price, moneyFormat = null) {
  var bodyRef;
  moneyFormat === null &&
    (moneyFormat =
      (bodyRef = document.body) == null
        ? void 0
        : bodyRef.getAttribute("data-money-format"));
  let cents;
  if (!price) return;
  typeof price == "string"
    ? (cents = Number(price.replace(".", "")))
    : (cents = price);
  let formattedValue = "";
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/,
    formatString = moneyFormat;
  if (!formatString) return;
  function formatWithDelimiters(
    number,
    precision = 2,
    thousands = ",",
    decimal = ".",
  ) {
    if (isNaN(number) || number == null) return "0";
    const parts = (number / 100).toFixed(precision).split("."),
      integerPart = parts[0].replace(
        /(\d)(?=(\d\d\d)+(?!\d))/g,
        `$1${thousands}`,
      ),
      decimalPart = parts[1] ? decimal + parts[1] : "";
    return integerPart + decimalPart;
  }
  setFunctionName(formatWithDelimiters, "formatWithDelimiters");
  const placeholderMatch = formatString.match(placeholderRegex);
  // Shopify ships eight money-format placeholders and every one of them must be handled here.
  // An unhandled placeholder leaves formattedValue empty, which strips the amount and renders bare currency such as " kr".
  if (placeholderMatch && placeholderMatch[1])
    switch (placeholderMatch[1]) {
      case "amount":
        formattedValue = formatWithDelimiters(cents, 2);
        break;
      case "amount_no_decimals":
        formattedValue = formatWithDelimiters(cents, 0);
        break;
      case "amount_with_comma_separator":
        formattedValue = formatWithDelimiters(cents, 2, ".", ",");
        break;
      case "amount_no_decimals_with_comma_separator":
        formattedValue = formatWithDelimiters(cents, 0, ".", ",");
        break;
      case "amount_with_apostrophe_separator":
        formattedValue = formatWithDelimiters(cents, 2, "'", ".");
        break;
      case "amount_no_decimals_with_space_separator":
        formattedValue = formatWithDelimiters(cents, 0, " ", ".");
        break;
      case "amount_with_space_separator":
        formattedValue = formatWithDelimiters(cents, 2, " ", ",");
        break;
      case "amount_with_period_and_space_separator":
        formattedValue = formatWithDelimiters(cents, 2, " ", ".");
        break;
      default:
        // A placeholder Shopify adds later must degrade to a readable number, never to nothing.
        formattedValue = formatWithDelimiters(cents, 2);
    }
  return formatString.replace(placeholderRegex, formattedValue);
}
setFunctionName(formatMoney, "formatMoney");
export {
  AutoresolvingOptionSelector,
  blog as Blog,
  ThemeComponent,
  cart,
  collection,
  dataLayerPush,
  formatMoney,
  hashUserDataForGoogle,
  scrollToElement,
  scrollToElementDuplicate as scrollToElement$1,
  setVariantUrlParam,
};
(() => {
  const knownComponents = [
    {
      "file": "component-AccordionBody.js",
      "tagName": "accordion-body",
    },
    {
      "file": "component-AccordionContainer.js",
      "tagName": "accordion-container",
    },
    {
      "file": "component-AccordionHeader.js",
      "tagName": "accordion-header",
    },
    {
      "file": "component-AnimationLayer.js",
      "tagName": "animation-layer",
    },
    {
      "file": "component-ArticleAuthor.js",
      "tagName": "article-author",
    },
    {
      "file": "component-ArticleCard.js",
      "tagName": "article-card",
    },
    {
      "file": "component-ArticleComments.js",
      "tagName": "article-comments",
    },
    {
      "file": "component-ArticleContent.js",
      "tagName": "article-content",
    },
    {
      "file": "component-ArticleHeader.js",
      "tagName": "article-header",
    },
    {
      "file": "component-ArticleNavigation.js",
      "tagName": "article-navigation",
    },
    {
      "file": "component-ArticleRelated.js",
      "tagName": "article-related",
    },
    {
      "file": "component-ArticleScrollBar.js",
      "tagName": "article-scroll-bar",
    },
    {
      "file": "component-BlogFilter.js",
      "tagName": "blog-filter",
    },
    {
      "file": "component-BlogMain.js",
      "tagName": "blog-main",
    },
    {
      "file": "component-BlogSearch.js",
      "tagName": "blog-search",
    },
    {
      "file": "component-BlogSearchFloating.js",
      "tagName": "blog-search-floating",
    },
    {
      "file": "component-BlogSearchResultsBar.js",
      "tagName": "blog-search-results-bar",
    },
    {
      "file": "component-BodyOverlay.js",
      "tagName": "body-overlay",
    },
    {
      "file": "component-BreadCrumbs.js",
      "tagName": "bread-crumbs",
    },
    {
      "file": "component-CartFeature.js",
      "tagName": "cart-feature",
    },
    {
      "file": "component-CartItem.js",
      "tagName": "cart-item",
    },
    {
      "file": "component-CartMain.js",
      "tagName": "cart-main",
    },
    {
      "file": "component-CartSummary.js",
      "tagName": "cart-summary",
    },
    {
      "file": "component-CollectionCard.js",
      "tagName": "collection-card",
    },
    {
      "file": "component-CollectionFilteringSorting.js",
      "tagName": "collection-filtering-sorting",
    },
    {
      "file": "component-CollectionMain.js",
      "tagName": "collection-main",
    },
    {
      "file": "component-CollectionSummary.js",
      "tagName": "collection-summary",
    },
    {
      "file": "component-ComparisonCharts.js",
      "tagName": "comparison-charts",
    },
    {
      "file": "component-ComparisonTable.js",
      "tagName": "comparison-table",
    },
    {
      "file": "component-ContactMain.js",
      "tagName": "contact-main",
    },
    {
      "file": "component-CustomerAddresses.js",
      "tagName": "customer-addresses",
    },
    {
      "file": "component-CustomerForm.js",
      "tagName": "customer-form",
    },
    {
      "file": "component-CustomerOverview.js",
      "tagName": "customer-overview",
    },
    {
      "file": "component-CustomerSidebar.js",
      "tagName": "customer-sidebar",
    },
    {
      "file": "component-DataLayerArticle.js",
      "tagName": "data-layer-article",
    },
    {
      "file": "component-DataLayerPageLoad.js",
      "tagName": "data-layer-page-load",
    },
    {
      "file": "component-DataTable.js",
      "tagName": "data-table",
    },
    {
      "file": "component-DeliveryCountdown.js",
      "tagName": "delivery-countdown",
    },
    {
      "file": "component-DrawerContainer.js",
      "tagName": "drawer-container",
    },
    {
      "file": "component-DrawerHeader.js",
      "tagName": "drawer-header",
    },
    {
      "file": "component-ThemeCarousel.js",
      "tagName": "theme-carousel",
    },
    {
      "file": "component-CarouselNav.js",
      "tagName": "carousel-nav",
    },
    {
      "file": "component-ErrorPage.js",
      "tagName": "error-page",
    },
    {
      "file": "component-FakeDropdown.js",
      "tagName": "fake-dropdown",
    },
    {
      "file": "component-FaqContainer.js",
      "tagName": "faq-container",
    },
    {
      "file": "component-FaqSearch.js",
      "tagName": "faq-search",
    },
    {
      "file": "component-FeaturedCarousel.js",
      "tagName": "featured-carousel",
    },
    {
      "file": "component-FeaturedCollectionCard.js",
      "tagName": "featured-collection-card",
    },
    {
      "file": "component-FeaturedCollectionsCarousel.js",
      "tagName": "featured-collections-carousel",
    },
    {
      "file": "component-FeaturedTile.js",
      "tagName": "featured-tile",
    },
    {
      "file": "component-FeaturedTileCarousel.js",
      "tagName": "featured-tile-carousel",
    },
    {
      "file": "component-FeaturedTileGrid.js",
      "tagName": "featured-tile-grid",
    },
    {
      "file": "component-FeaturedTileRow.js",
      "tagName": "featured-tile-row",
    },
    {
      "file": "component-FeaturedVideo.js",
      "tagName": "featured-video",
    },
    {
      "file": "component-GiftCard.js",
      "tagName": "gift-card",
    },
    {
      "file": "component-HeroBanner.js",
      "tagName": "hero-banner",
    },
    {
      "file": "component-IconCircle.js",
      "tagName": "icon-circle",
    },
    {
      "file": "component-IconCount.js",
      "tagName": "icon-count",
    },
    {
      "file": "component-IconTextGrid.js",
      "tagName": "icon-text-grid",
    },
    {
      "file": "component-ImageHighlights.js",
      "tagName": "image-highlights",
    },
    {
      "file": "component-ImageHighlightsTaper.js",
      "tagName": "image-highlights-taper",
    },
    {
      "file": "component-ImageSlider.js",
      "tagName": "image-slider",
    },
    {
      "file": "component-ImageText.js",
      "tagName": "image-text",
    },
    {
      "file": "component-ImageZoom.js",
      "tagName": "image-zoom",
    },
    {
      "file": "component-LoadMore.js",
      "tagName": "load-more",
    },
    {
      "file": "component-LoadingSpinner.js",
      "tagName": "loading-spinner",
    },
    {
      "file": "component-LocalPickup.js",
      "tagName": "local-pickup",
    },
    {
      "file": "component-MapLocation.js",
      "tagName": "map-location",
    },
    {
      "file": "component-MasonryGrid.js",
      "tagName": "masonry-grid",
    },
    {
      "file": "component-MegaMenu.js",
      "tagName": "mega-menu",
    },
    {
      "file": "component-MobileMenu.js",
      "tagName": "mobile-menu",
    },
    {
      "file": "component-NewsletterForm.js",
      "tagName": "newsletter-form",
    },
    {
      "file": "component-PageHeader.js",
      "tagName": "page-header",
    },
    {
      "file": "component-PaginationBar.js",
      "tagName": "pagination-bar",
    },
    {
      "file": "component-PasswordMain.js",
      "tagName": "password-main",
    },
    {
      "file": "component-PredictiveSearch.js",
      "tagName": "predictive-search",
    },
    {
      "file": "component-PriceFilters.js",
      "tagName": "price-filters",
    },
    {
      "file": "component-ProductButtonOption.js",
      "tagName": "product-button-option",
    },
    {
      "file": "component-ProductCard.js",
      "tagName": "product-card",
    },
    {
      "file": "component-ProductCardBadges.js",
      "tagName": "product-card-badges",
    },
    {
      "file": "component-ProductCardQuickAdd.js",
      "tagName": "product-card-quick-add",
    },
    {
      "file": "component-ProductCardQuickAddTrigger.js",
      "tagName": "product-card-quick-add-trigger",
    },
    {
      "file": "component-ProductCardSwatches.js",
      "tagName": "product-card-swatches",
    },
    {
      "file": "component-ProductDetails.js",
      "tagName": "product-details",
    },
    {
      "file": "component-ProductDropdownOption.js",
      "tagName": "product-dropdown-option",
    },
    {
      "file": "component-ProductFilters.js",
      "tagName": "product-filters",
    },
    {
      "file": "component-ProductForm.js",
      "tagName": "product-form",
    },
    {
      "file": "component-ProductGallery.js",
      "tagName": "product-gallery",
    },
    {
      "file": "component-ProductHighlights.js",
      "tagName": "product-highlights",
    },
    {
      "file": "component-ProductMain.js",
      "tagName": "product-main",
    },
    {
      "file": "component-ProductNavigation.js",
      "tagName": "product-navigation",
    },
    {
      "file": "component-ProductOptionPicker.js",
      "tagName": "product-option-picker",
    },
    {
      "file": "component-ProductStickyBar.js",
      "tagName": "product-sticky-bar",
    },
    {
      "file": "component-ProductTabs.js",
      "tagName": "product-tabs",
    },
    {
      "file": "component-ProgramCategories.js",
      "tagName": "program-categories",
    },
    {
      "file": "component-PromotionTile.js",
      "tagName": "promotion-tile",
    },
    {
      "file": "component-QuantitySelector.js",
      "tagName": "quantity-selector",
    },
    {
      "file": "component-RecipientForm.js",
      "tagName": "recipient-form",
    },
    {
      "file": "component-RecommendedProducts.js",
      "tagName": "recommended-products",
    },
    {
      "file": "component-ResponsiveTable.js",
      "tagName": "responsive-table",
    },
    {
      "file": "component-RichText.js",
      "tagName": "rich-text",
    },
    {
      "file": "component-ScrollToElement.js",
      "tagName": "scroll-to-element",
    },
    {
      "file": "component-SearchField.js",
      "tagName": "search-field",
    },
    {
      "file": "component-SearchResults.js",
      "tagName": "search-results",
    },
    {
      "file": "component-SectionHeader.js",
      "tagName": "section-header",
    },
    {
      "file": "component-SectionHeaderTabs.js",
      "tagName": "section-header-tabs",
    },
    {
      "file": "component-ShopLocalization.js",
      "tagName": "shop-localization",
    },
    {
      "file": "component-SideCart.js",
      "tagName": "side-cart",
    },
    {
      "file": "component-SiteFooter.js",
      "tagName": "site-footer",
    },
    {
      "file": "component-SiteHeader.js",
      "tagName": "site-header",
    },
    {
      "file": "component-SiteHeaderWrapper.js",
      "tagName": "site-header-wrapper",
    },
    {
      "file": "component-SocialLinks.js",
      "tagName": "social-links",
    },
    {
      "file": "component-TestimonialGrid.js",
      "tagName": "testimonial-grid",
    },
    {
      "file": "component-TestimonialSingle.js",
      "tagName": "testimonial-single",
    },
    {
      "file": "component-TextTicker.js",
      "tagName": "text-ticker",
    },
    {
      "file": "component-TileCarousel.js",
      "tagName": "tile-carousel",
    },
    {
      "file": "component-TopBar.js",
      "tagName": "top-bar",
    },
    {
      "file": "component-UspBar.js",
      "tagName": "usp-bar",
    },
    {
      "file": "component-VideoPlayer.js",
      "tagName": "video-player",
    },
  ];
  const pendingComponents = knownComponents.map((target) => ({
    tagName: target.tagName,
    file: target.file,
    elements: document.getElementsByTagName(target.tagName),
    loading: false,
  }));
  // Component stylesheets live in their own .css assets (extracted from the JS).
  // The map is emitted by theme-head.liquid; presence in it is the "has styles"
  // flag. Links append to BODY, not head - late-in-document order is load-bearing
  // (component styles must keep winning specificity ties against head styles).
  let componentStyles = {};
  try {
    componentStyles = JSON.parse(
      document.getElementById("component-styles-map")?.textContent ?? "{}",
    );
  } catch {
    componentStyles = {};
  }
  const checkComponents = () => {
    for (const comp of pendingComponents.filter((c) => !c.loading)) {
      if (comp.elements.length < 1) {
        // HTMLCollection is live
        continue;
      }
      comp.loading = true;
      const styleHref = componentStyles[comp.file.replace(/\.js$/, ".css")];
      if (styleHref && !document.querySelector(`link[href="${styleHref}"]`)) {
        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = styleHref;
        document.body.appendChild(link);
      }
      import(`./${comp.file}`).then(() => {
        window.componentService.bootstrap();
      });
    }
  };
  const componentObserver = new MutationObserver((mutations, observer) => {
    checkComponents();
  });
  componentObserver.observe(document.body, {
    attributes: false,
    characterData: false,
    childList: true,
    subtree: true,
  });
  checkComponents();
})();
